import { z } from "zod";

import {
  connectorAuthMethodIdSchema,
  connectorSlugSchema,
} from "./connector-identity";

/**
 * Connector response schema
 */
export const connectorResponseConnectionStatusSchema = z.enum([
  "connected",
  "reconnect-required",
]);

export type ConnectorResponseConnectionStatus = z.infer<
  typeof connectorResponseConnectionStatusSchema
>;

export const connectorReconnectReasonSchema = z.enum([
  "provider_session_expired",
  "authorization_expired_or_revoked",
  "credential_expired",
]);

export type ConnectorReconnectReason = z.infer<
  typeof connectorReconnectReasonSchema
>;

export const connectorResponseSchema = z.object({
  id: z.uuid(),
  slug: connectorSlugSchema,
  authMethod: connectorAuthMethodIdSchema,
  externalId: z.string().nullable(),
  externalUsername: z.string().nullable(),
  externalEmail: z.string().nullable(),
  oauthScopes: z.array(z.string()).nullable(),
  connectionStatus: connectorResponseConnectionStatusSchema,
  reconnectReason: connectorReconnectReasonSchema.nullable().default(null),
  tokenExpiresAt: z.string().nullable().default(null),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type ConnectorResponse = z.infer<typeof connectorResponseSchema>;

export const connectorProvidedBindingNamespaceSchema = z.enum([
  "secrets",
  "vars",
]);

export const connectorProvidedBindingSourceSchema = z.discriminatedUnion(
  "kind",
  [
    z.object({
      kind: z.literal("connector-secret"),
      name: z.string(),
    }),
    z.object({
      kind: z.literal("connector-variable"),
      name: z.string(),
    }),
  ],
);

export const connectorProvidedBindingSchema = z.object({
  connectorSlug: connectorSlugSchema,
  authMethod: connectorAuthMethodIdSchema,
  namespace: connectorProvidedBindingNamespaceSchema,
  name: z.string(),
  optional: z.boolean(),
  source: connectorProvidedBindingSourceSchema,
});

export type ConnectorProvidedBinding = z.infer<
  typeof connectorProvidedBindingSchema
>;
export type ConnectorProvidedBindingNamespace = z.infer<
  typeof connectorProvidedBindingNamespaceSchema
>;

/**
 * Names that a stored connector guarantees at runtime. Optional bindings are
 * omitted because they describe possible connector supply, not guaranteed
 * connector supply.
 */
export function guaranteedConnectorProvidedBindingNames(args: {
  readonly bindings: readonly ConnectorProvidedBinding[];
  readonly namespace: ConnectorProvidedBindingNamespace;
}): Set<string> {
  const names = new Set<string>();
  for (const binding of args.bindings) {
    if (binding.namespace === args.namespace && !binding.optional) {
      names.add(binding.name);
    }
  }
  return names;
}

/**
 * List connectors response
 */
export const connectorListResponseSchema = z.object({
  connectors: z.array(connectorResponseSchema),
  configuredConnectorSlugs: z.array(connectorSlugSchema),
  connectorProvidedBindings: z
    .array(connectorProvidedBindingSchema)
    .default([]),
});

export type ConnectorListResponse = z.infer<typeof connectorListResponseSchema>;

/**
 * Scope diff response schema
 */
export const scopeDiffResponseSchema = z.object({
  addedScopes: z.array(z.string()),
  removedScopes: z.array(z.string()),
  currentScopes: z.array(z.string()),
  storedScopes: z.array(z.string()),
});

export type ScopeDiffResponse = z.infer<typeof scopeDiffResponseSchema>;

export const connectorOauthStartResponseSchema = z.object({
  authorizationUrl: z.string(),
});

export type ConnectorOauthStartResponse = z.infer<
  typeof connectorOauthStartResponseSchema
>;

export const connectorOauthDeviceAuthSessionStartResponseSchema = z.object({
  sessionId: z.uuid(),
  sessionToken: z.string(),
  connectorSlug: connectorSlugSchema,
  status: z.literal("pending"),
  userCode: z.string(),
  verificationUri: z.string(),
  verificationUriComplete: z.string().optional(),
  expiresIn: z.number(),
  interval: z.number(),
});

export type ConnectorOauthDeviceAuthSessionStartResponse = z.infer<
  typeof connectorOauthDeviceAuthSessionStartResponseSchema
>;

export const connectorOauthDeviceAuthSessionPollRequestSchema = z.object({
  sessionToken: z.string(),
});

export type ConnectorOauthDeviceAuthSessionPollRequest = z.infer<
  typeof connectorOauthDeviceAuthSessionPollRequestSchema
>;

export const connectorOauthDeviceAuthSessionPollResponseSchema =
  z.discriminatedUnion("status", [
    z.object({
      status: z.literal("pending"),
      interval: z.number(),
    }),
    z.object({
      status: z.literal("complete"),
      connector: connectorResponseSchema,
    }),
    z.object({
      status: z.literal("denied"),
      errorCode: z.string().optional(),
      errorMessage: z.string().optional(),
    }),
    z.object({
      status: z.literal("expired"),
      errorCode: z.string().optional(),
      errorMessage: z.string().optional(),
    }),
    z.object({
      status: z.literal("error"),
      errorCode: z.string().optional(),
      errorMessage: z.string().optional(),
    }),
  ]);

export type ConnectorOauthDeviceAuthSessionPollResponse = z.infer<
  typeof connectorOauthDeviceAuthSessionPollResponseSchema
>;

export const connectorExternalCodeSessionStartResponseSchema = z.object({
  sessionId: z.uuid(),
  sessionToken: z.string(),
  connectorSlug: connectorSlugSchema,
  status: z.literal("pending"),
  authorizationUrl: z.string(),
  expiresIn: z.number(),
});

export type ConnectorExternalCodeSessionStartResponse = z.infer<
  typeof connectorExternalCodeSessionStartResponseSchema
>;

export const connectorExternalCodeSessionCompleteRequestSchema = z.object({
  sessionToken: z.string(),
  code: z.string().trim().min(1).max(4096),
});

export type ConnectorExternalCodeSessionCompleteRequest = z.infer<
  typeof connectorExternalCodeSessionCompleteRequestSchema
>;

export const connectorExternalCodeSessionCompleteResponseSchema = z.object({
  status: z.literal("complete"),
  connector: connectorResponseSchema,
});

export type ConnectorExternalCodeSessionCompleteResponse = z.infer<
  typeof connectorExternalCodeSessionCompleteResponseSchema
>;
